/**
 * Supabase / PostgREST Fehler-Formatierer für die Fallübersicht und andere
 * datenintensive Listen. Standardmäßig schluckt `err.message` jede Diagnose —
 * Code, Details und Hint gehen verloren und landen als generisches
 * "Fehler beim Laden" in der UI. Siehe Issue #411.
 *
 * Diese Helfer geben eine strukturierte Meldung für Toasts/Error-States und
 * erkennen benigne Zustände wie einen stale PostgREST-Schema-Cache (tritt
 * direkt nach einer Migration auf), in denen wir gracefully zu einer leeren
 * Liste fallen können statt einen roten Error zu zeigen.
 */

interface MaybePostgrestError {
  code?: string | null;
  message?: string | null;
  details?: string | null;
  hint?: string | null;
}

function asErrorLike(err: unknown): MaybePostgrestError | null {
  if (!err || typeof err !== 'object') return null;
  return err as MaybePostgrestError;
}

export function formatSupabaseError(err: unknown): string {
  if (err == null) return 'Unbekannter Fehler';
  if (typeof err === 'string') return err || 'Unbekannter Fehler';
  if (err instanceof Error) {
    const maybe = err as unknown as MaybePostgrestError;
    return buildMessage(maybe.code, err.message, maybe.details, maybe.hint)
      || 'Unbekannter Fehler';
  }
  const e = asErrorLike(err);
  if (!e) return 'Unbekannter Fehler';
  return buildMessage(e.code, e.message, e.details, e.hint) || 'Unbekannter Fehler';
}

function buildMessage(
  code: string | null | undefined,
  message: string | null | undefined,
  details: string | null | undefined,
  hint: string | null | undefined,
): string {
  const parts: string[] = [];
  if (code) parts.push(String(code));
  if (message) parts.push(String(message));
  const head = parts.join(' — ');
  const tail: string[] = [];
  if (details) tail.push(String(details));
  if (hint) tail.push(String(hint));
  if (tail.length === 0) return head;
  return head ? `${head} (${tail.join('; ')})` : tail.join('; ');
}

/**
 * Direkt nach einem Migrations-Deploy braucht PostgREST einige Sekunden, bis
 * der Schema-Cache die neue Spalte kennt. In dem Fenster werfen Queries auf
 * die neue Spalte PGRST204/PGRST205 oder eine "schema cache"-Meldung. Das ist
 * temporär — wir behandeln es als "leeres Ergebnis" statt als harten Fehler.
 */
export function isSchemaCacheStale(err: unknown): boolean {
  const e = asErrorLike(err);
  if (!e) return false;
  const code = (e.code || '').toString();
  const msg = (e.message || '').toLowerCase();
  if (code === 'PGRST204' || code === 'PGRST205') return true;
  return msg.includes('schema cache');
}

/**
 * Erkennt "column does not exist" (Postgres 42703) oder den PostgREST-Cache-
 * Miss für eine bestimmte Spalte. Wenn `expectedColumn` gesetzt ist, wird
 * zusätzlich geprüft, dass genau diese Spalte in der Message erwähnt wird.
 */
export function isMissingColumnError(err: unknown, expectedColumn?: string): boolean {
  const e = asErrorLike(err);
  if (!e) return false;
  const code = (e.code || '').toString();
  const msg = (e.message || '').toLowerCase();
  const mentionsMissing =
    code === '42703'
    || msg.includes('does not exist')
    || msg.includes('could not find') && msg.includes('column');
  if (!mentionsMissing) return false;
  if (!expectedColumn) return true;
  return msg.includes(expectedColumn.toLowerCase());
}
